import classNames from "classnames/bind";
import { useEffect, useState } from "react";

import Button from "common/blocks/Button";
import Popup from "common/blocks/Modal/Popup";
import UIIcon from "common/blocks/UIIcon";
import { useFavouritesContext } from "features/favourites/hooks/useFavouritesContext";
import type { ResolvedConfirmModal } from "resolvers/data/confirmModalResolver";
import styles from "./ShortlistButton.module.scss";
import { Vehicle } from "schema-dts";
import { VehicleService } from "services/vehicle/vehicle";
import generateGtmEcommerceEntryString from "utils/generateGtmEcommerceEntryString";
import { ResponseVehicle } from "services/motorpoint/shortlist";

const cx = classNames.bind(styles);

export type ShortlistButtonProps = {
    isSaved?: boolean;
    isConfirmationNeeded?: boolean;
    vehicleId: number;
    title?: string;
    text?: string;
    confirmButtonText?: string;
    removeVehicleModal?: ResolvedConfirmModal;
    large?: boolean;
    className?: string;
    tagManagerEventData?: {
        vehicle: VehicleService.Vehicle | ResponseVehicle;
        googlePlaceId?: string;
    };
};

const ShortlistButton: React.FC<ShortlistButtonProps> = ({
    isSaved,
    vehicleId,
    isConfirmationNeeded,
    removeVehicleModal,
    large = false,
    className,
    tagManagerEventData = null,
}) => {
    const { shortlistApi } = useFavouritesContext();
    const [isChecked, setIsChecked] = useState<boolean>(false);
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

    const handleChange = () => {
        if (!isChecked) {
            setIsChecked((val) => !val);
            shortlistApi.addToList(vehicleId);

            if (tagManagerEventData) {
                const { vehicle, googlePlaceId } = tagManagerEventData;
                window.dataLayer = window.dataLayer || [];
                const dataObject = generateGtmEcommerceEntryString(
                    {
                        vehicle,
                        itemListId: "pdp-favourite-button",
                        event: "add_to_wishlist",
                        googlePlaceId,
                    },
                    true
                ) as object;

                window.dataLayer.push(dataObject);
            }
        } else if (isConfirmationNeeded) {
            setIsModalOpen(true);
        } else {
            shortlistApi.removeFromList(vehicleId);
            setIsChecked((val) => !val);
        }
    };

    useEffect(() => {
        setIsChecked(!!isSaved);
    }, [isSaved]);

    const handleConfirm = () => {
        shortlistApi.removeFromList(vehicleId);
        setIsChecked((val) => !val);
        setIsModalOpen(false);
    };

    return (
        <label
            className={`${cx("checkbox", { checked: !isChecked, large })}${
                className ? ` ${className}` : ""
            } `}
        >
            <input
                type="checkbox"
                className={cx("inputCheck")}
                onChange={handleChange}
                checked={isChecked}
                aria-label={isChecked ? "Remove from shortlist" : "Add to shortlist"}
            />
            <UIIcon icon="Heart" className={cx("buttonIcon")} />
            <Popup
                contentLabel="removeVehicleModal"
                isOpen={isModalOpen}
                setIsOpen={setIsModalOpen}
                className={cx("popup")}
            >
                <div>
                    <h3 className={cx("title")}>{removeVehicleModal?.title}</h3>
                    <p className={cx("text")}>{removeVehicleModal?.text}</p>
                    <div className={cx("controls")}>
                        <Button theme="accent" className={cx("button")} onClick={handleConfirm}>
                            {removeVehicleModal?.confirmButtonText}
                        </Button>
                        <Button
                            theme="textLink"
                            className={cx("button", "defaultButton")}
                            onClick={() => setIsModalOpen(false)}
                        >
                            Cancel
                        </Button>
                    </div>
                </div>
            </Popup>
        </label>
    );
};

export default ShortlistButton;
